import { useCallback, useMemo, useState } from 'react';
import { ConfigProvider, Input, Modal, Tooltip } from 'antd';
import heIL from 'antd/locale/he_IL';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ProTable, type ProColumns } from '@ant-design/pro-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faBan } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import type { DepositSummary } from '@deposits/shared';
import { Money } from '../components/Money';
import {
  multiSelectColumn,
  multiSelectColumnFixed,
  numericRangeColumn,
} from '../components/tableHelpers';
import { EmptyState } from '../components/EmptyState';
import { Loader, loaderIndicator } from '../components/Loader';
import ReportingPeriodFilter from '../components/ReportingPeriodFilter';
import { useRegisterRefresh } from '../components/PageActions';
import { useLocationContext } from '../auth/LocationContext';
import { useProjectContext } from '../auth/ProjectContext';
import { useCancelDeposit, useDepositsList } from '../hooks/useDeposits';
import {
  firstOfMonthIsrael,
  formatTime,
  isoToDisplay,
  lastOfMonthIsrael,
} from '../utils/dates';

type StatusFilterValue = 'active' | 'cancelled';

interface Props {
  /** Open the in-app PDF drawer for this deposit. Lifted to App so create-
      success and Tab B actions both target the same drawer. */
  onOpenPdf: (depositId: number) => void;
}

export function DepositsReportTab({ onOpenPdf }: Props) {
  const { selectedLocationId } = useLocationContext();
  const { selectedProjectId } = useProjectContext();
  const [period, setPeriod] = useState<{ from: string; to: string }>(() => ({
    from: firstOfMonthIsrael(),
    to: lastOfMonthIsrael(),
  }));
  const [search, setSearch] = useState('');
  const [statusFilters, setStatusFilters] = useState<StatusFilterValue[]>([]);

  // Server-side status filter follows the column filter; if neither value is
  // ticked OR both are ticked, we send 'all'.
  const statusParam: 'all' | 'active' | 'cancelled' = useMemo(() => {
    if (statusFilters.length === 0 || statusFilters.length === 2) return 'all';
    return statusFilters[0]!;
  }, [statusFilters]);

  const params = useMemo(() => {
    if (selectedLocationId == null) return null;
    return {
      location_id: selectedLocationId,
      project_id: selectedProjectId ?? undefined,
      from: period.from,
      to: period.to,
      search: search.trim() || undefined,
      status: statusParam,
    };
  }, [selectedLocationId, selectedProjectId, period, search, statusParam]);

  const { data: deposits = [], isFetching, isError, refetch } = useDepositsList(params);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);
  useRegisterRefresh(handleRefresh);

  const cancelMutation = useCancelDeposit();
  const confirmCancel = useCallback(
    (deposit: DepositSummary) => {
      Modal.confirm({
        title: `ביטול הפקדה מספר ${deposit.deposit_number}`,
        content: (
          <div className="text-small">
            <div>הביטול ישחרר את התקבולים בחזרה ללשונית "תקבולים להפקדה".</div>
            <div className="mt-2 text-secondary-text">
              מספר ההפקדה {deposit.deposit_number} לא ישתמש שוב — הוא יישאר רשום
              כמבוטל. אי אפשר להפוך את הפעולה.
            </div>
          </div>
        ),
        okText: 'בטל הפקדה',
        cancelText: 'חזרה',
        okButtonProps: { danger: true },
        direction: 'rtl',
        onOk: async () => {
          try {
            await cancelMutation.mutateAsync({ id: deposit.id });
            toast.success(`הפקדה מספר ${deposit.deposit_number} בוטלה`);
          } catch (err) {
            toast.error((err as Error).message || 'שגיאה בביטול ההפקדה');
            // Re-throw so the Modal stays open and the user can retry / close.
            throw err;
          }
        },
      });
    },
    [cancelMutation],
  );

  const columns: ProColumns<DepositSummary>[] = [
    {
      title: 'תאריך הפקדה',
      dataIndex: 'deposit_date',
      width: 130,
      render: (_, r) => isoToDisplay(r.deposit_date),
      sorter: (a, b) => a.deposit_date.localeCompare(b.deposit_date),
    },
    {
      title: 'שעת יצירה',
      dataIndex: 'created_at',
      width: 90,
      render: (_, r) => formatTime(r.created_at),
      sorter: (a, b) => a.created_at.localeCompare(b.created_at),
    },
    {
      title: 'מספר הפקדה',
      dataIndex: 'deposit_number',
      width: 110,
      render: (_, r) => <span dir="ltr">{r.deposit_number}</span>,
      sorter: (a, b) => a.deposit_number - b.deposit_number,
      // Default sort: largest deposit_number first. Matches the server query's
      // ORDER BY (so the initial render is already in this order); the visible
      // arrow on the header tells the user that's what's sorted.
      defaultSortOrder: 'descend',
      ...multiSelectColumn<DepositSummary>(deposits, (r) => r.deposit_number),
    },
    {
      title: 'סה"כ מזומן',
      dataIndex: 'cash_total',
      width: 120,
      render: (_, r) => <Money amount={r.cash_total} />,
      sorter: (a, b) => a.cash_total - b.cash_total,
      ...numericRangeColumn<DepositSummary>((r) => r.cash_total),
    },
    {
      title: 'סה"כ צ\'קים',
      dataIndex: 'checks_total',
      width: 120,
      render: (_, r) => <Money amount={r.checks_total} />,
      sorter: (a, b) => a.checks_total - b.checks_total,
      ...numericRangeColumn<DepositSummary>((r) => r.checks_total),
    },
    {
      title: 'סה"כ בהפקדה',
      dataIndex: 'grand_total',
      width: 130,
      render: (_, r) => (
        <span className="font-semibold">
          <Money amount={r.grand_total} />
        </span>
      ),
      sorter: (a, b) => a.grand_total - b.grand_total,
      ...numericRangeColumn<DepositSummary>((r) => r.grand_total),
    },
    {
      title: 'סטטוס',
      dataIndex: 'cancelled_at',
      width: 80,
      render: (_, r) =>
        r.cancelled_at ? (
          <span className="text-error-red font-semibold">מבוטל</span>
        ) : (
          <span className="text-success-green">הופקד</span>
        ),
      // Sort by the displayed label so הופקד < מבוטל groups by status.
      sorter: (a, b) =>
        (a.cancelled_at ? 'מבוטל' : 'הופקד').localeCompare(
          b.cancelled_at ? 'מבוטל' : 'הופקד',
          'he',
        ),
      // The dropdown uses the Arbox-Expenses style via multiSelectColumnFixed.
      // We override `onFilter` to always pass (filtering happens server-side
      // via the statusFilters → statusParam plumbing); `filteredValue` keeps
      // the dropdown in sync with the parent's state.
      ...multiSelectColumnFixed<DepositSummary>(
        [
          { value: 'active', label: 'הופקד' },
          { value: 'cancelled', label: 'מבוטל' },
        ],
        () => true,
      ),
      filteredValue: statusFilters,
    },
    {
      // The staff_members pick — the person who physically went to deposit.
      // May be null (no one selected at create time).
      title: 'מפקיד',
      dataIndex: 'depositor_name',
      width: 180,
      ellipsis: true,
      render: (_, r) => r.depositor_name || '—',
      sorter: (a, b) =>
        (a.depositor_name || '').localeCompare(b.depositor_name || '', 'he'),
      ...multiSelectColumn<DepositSummary>(deposits, (r) => r.depositor_name),
    },
    {
      // Whoever clicked Create Deposit (= the session user). Always present.
      title: 'יוצר ההפקדה',
      dataIndex: 'created_by_name',
      width: 180,
      ellipsis: true,
      render: (_, r) => r.created_by_name || '—',
      sorter: (a, b) =>
        (a.created_by_name || '').localeCompare(b.created_by_name || '', 'he'),
      ...multiSelectColumn<DepositSummary>(deposits, (r) => r.created_by_name),
    },
    {
      title: 'הערות',
      dataIndex: 'notes',
      width: 180,
      ellipsis: true,
      render: (_, r) => r.notes || '—',
      sorter: (a, b) => (a.notes || '').localeCompare(b.notes || '', 'he'),
      ...multiSelectColumn<DepositSummary>(deposits, (r) => r.notes),
    },
    {
      title: 'פעולות',
      key: 'actions',
      width: 200,
      // RTL: antd treats `fixed` as logical start/end, so `'right'` pins to
      // the end of the row (left edge visually) — where the user expects
      // actions to live.
      fixed: 'right',
      render: (_, r) => {
        const isCancelled = r.cancelled_at != null;
        // Any deposit can be cancelled (regardless of sequence position).
        // Cancelled deposits leave a gap in the deposit_number sequence; that
        // gap is intentional and documented on the slip. New deposits keep
        // counting from the current max — no renumbering.
        const cancelDisabled = isCancelled;
        const cancelTooltip = isCancelled ? 'הפקדה זו כבר בוטלה' : '';
        return (
          <div className="flex items-center gap-3">
            <ActionLink
              icon={faEye}
              label="צפה"
              disabled={r.pdf_status === 'failed'}
              tooltip={r.pdf_status === 'failed' ? 'הפקת ה-PDF נכשלה — צור הפקדה מחדש' : ''}
              onClick={() => onOpenPdf(r.id)}
            />
            <ActionLink
              icon={faBan}
              label="ביטול"
              disabled={cancelDisabled}
              tooltip={cancelTooltip}
              danger
              onClick={() => confirmCancel(r)}
            />
          </div>
        );
      },
    },
  ];

  if (selectedLocationId == null) {
    return <Loader fullScreen />;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Filter bar */}
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap pb-3">
        <ReportingPeriodFilter
          value={period}
          onChange={(from, to) => setPeriod({ from, to })}
          buttonPrefix="תאריך הפקדה"
        />
        <Input.Search
          placeholder="חיפוש לפי מספר הפקדה"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 220 }}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
          <ProTable<DepositSummary>
            rowKey="id"
            columns={columns}
            dataSource={deposits}
            loading={
              isFetching ? { spinning: true, indicator: loaderIndicator('md') } : false
            }
            search={false}
            options={false}
            showSorterTooltip={false}
            defaultSize="small"
            onChange={(_pag, filters) => {
              const next = (filters.cancelled_at as StatusFilterValue[] | null) ?? [];
              setStatusFilters(next);
            }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              size: 'small',
              position: ['bottomLeft'],
              className: 'he-pagination',
              itemRender: (_page, type, original) => {
                if (type === 'prev')
                  return (
                    <button type="button" className="ant-pagination-item-link">
                      <RightOutlined />
                    </button>
                  );
                if (type === 'next')
                  return (
                    <button type="button" className="ant-pagination-item-link">
                      <LeftOutlined />
                    </button>
                  );
                return original;
              },
              locale: heIL.Pagination,
            }}
            locale={{
              emptyText: isError ? (
                <EmptyState
                  title="שגיאה בטעינת הפקדות"
                  description="נסה שוב או רענן את העמוד"
                />
              ) : (
                <EmptyState
                  title="אין הפקדות בטווח שנבחר"
                  description="צור הפקדה חדשה מהטאב 'תקבולים להפקדה'"
                />
              ),
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
            tableStyle={{ background: 'white' }}
            {...({ getPopupContainer: () => document.body } as any)}
          />
        </ConfigProvider>
      </div>

      {/* PDF drawer is rendered at the App level; we only trigger it via onOpenPdf. */}
    </div>
  );
}

function ActionLink({
  icon,
  label,
  onClick,
  disabled,
  tooltip,
  danger,
}: {
  icon: any;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
  danger?: boolean;
}) {
  const inner = (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 text-small font-medium transition-opacity ${
        disabled
          ? 'text-secondary-text cursor-not-allowed opacity-60'
          : danger
            ? 'text-error-red hover:opacity-70'
            : 'text-main-text hover:opacity-70'
      }`}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 13 }} />
      <span>{label}</span>
    </button>
  );
  if (tooltip && disabled) return <Tooltip title={tooltip}>{inner}</Tooltip>;
  return inner;
}
